import React from 'react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { LEVEL_META, LEVEL_ORDER, fmtK, fmtDate, LevelBadge, fieldCls } from './bbShared';

export const LevelSelect = ({ value, onChange, testId, className = '' }) => (
  <select value={value || 'tiede'} onChange={e => onChange(e.target.value)} onClick={e => e.stopPropagation()} data-testid={testId}
    className={`${fieldCls} !py-1.5 !text-xs font-bold ${className}`} style={{ background: LEVEL_META[value]?.bg, color: LEVEL_META[value]?.fg }}>
    {LEVEL_ORDER.map(k => <option key={k} value={k}>{LEVEL_META[k].label} · {LEVEL_META[k].pct}</option>)}
  </select>
);

export const LevelCounters = ({ levels, onPick, dark = true, testId = 'bb-level-counters' }) => (
  <div className={`grid grid-cols-2 sm:grid-cols-5 ${dark ? 'divide-white/10 sm:divide-x' : 'gap-2'}`} data-testid={testId}>
    {LEVEL_ORDER.map(k => {
      const m = LEVEL_META[k];
      const l = levels[k] || { count: 0, run_rate: 0 };
      return (
        <button key={k} type="button" onClick={() => onPick(k)} data-testid={`${testId}-${k}`}
          className={`text-left p-4 sm:p-5 transition-colors ${dark ? 'hover:bg-white/5' : 'rounded-xl bg-white hover:bg-[#EEECE8]'}`}>
          <p className="text-sm sm:text-base font-bold uppercase tracking-[0.08em] flex items-center gap-1.5" style={{ color: m.bar }}><m.icon className="w-4 h-4" />{m.label} · {m.pct}</p>
          <p className={`text-5xl sm:text-6xl font-bold leading-none mt-3 ${dark ? 'text-white' : 'text-[#1B1C1F]'}`} style={{ fontFamily: 'var(--font-display)' }}>{l.count}</p>
          <p className={`text-base sm:text-lg font-semibold mt-2 ${dark ? 'text-white/85' : 'text-slate-600'}`}>{fmtK(l.run_rate)} <span className="font-normal text-sm">run rate</span></p>
          <p className={`text-sm mt-1 underline ${dark ? 'text-white/70' : 'text-slate-500'}`}>voir la liste</p>
        </button>
      );
    })}
  </div>
);

export const LevelListDialog = ({ level, person, dossiers, onClose, onOpen, showCommercial = true }) => {
  const rows = (dossiers || []).filter(d => (!level || d.niveau === level) && (!person || d.commercial_id === person.commercial_id))
    .sort((a, b) => (a.sim?.date_signature_estimee || '9999').localeCompare(b.sim?.date_signature_estimee || '9999'));
  return (
    <Dialog open={!!(level || person)} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="bb-level-list-dialog">
        <DialogTitle className="flex items-center gap-2 flex-wrap">{level ? <LevelBadge level={level} /> : <b>{person?.name}</b>} {rows.length} dossier{rows.length > 1 ? 's' : ''} · {fmtK(rows.reduce((s, d) => s + (d.sim?.run_rate || 0), 0))} de CA prévisionnel</DialogTitle>
        {rows.length === 0 ? <p className="text-sm text-slate-500 py-6 text-center">Aucun dossier à ce niveau.</p> : (
          <>
          <div className="sm:hidden space-y-2" data-testid="bb-level-list-mobile">
            {rows.map(d => (
              <button key={d.id} type="button" onClick={() => onOpen(d.id)} className="w-full text-left rounded-xl border border-[#E5E3DE] p-3" data-testid={`bb-level-card-${d.id}`}>
                <div className="flex justify-between gap-2"><b className="text-sm">{d.sim?.compte || d.snapshot_entree?.compte}</b><b className="text-sm">{fmtK(d.sim?.run_rate)}</b></div>
                {!level && <div className="mt-1"><LevelBadge level={d.niveau} /></div>}
                <p className="text-xs text-slate-500 mt-1">
                  Signature : <span className={d.alerts?.date_manquante ? 'text-[#A1281D] font-semibold' : ''}>{d.sim?.date_signature_estimee ? fmtDate(d.sim.date_signature_estimee) : 'À compléter'}</span> · Ouverture : {fmtDate(d.sim?.date_ouverture)} · Com. {d.sim?.commission ?? '—'} %
                </p>
              </button>
            ))}
          </div>
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm min-w-[680px]">
              <thead><tr className="text-[10px] uppercase tracking-[0.12em] text-slate-500 text-left align-bottom">
                <th className="py-2 pr-3">Client</th>{!level && <th className="pr-3">Niveau</th>}{showCommercial && !person && <th className="pr-3">Commercial</th>}<th className="pr-3">Signature prév.</th><th className="pr-3">Ouverture prév.</th><th className="pr-3 text-right">Taux de com.</th><th className="text-right">CA prévisionnel</th>
              </tr></thead>
              <tbody>
                {rows.map(d => (
                  <tr key={d.id} className="border-t border-[#EEECE8] cursor-pointer hover:bg-[#F3F2EE]" onClick={() => onOpen(d.id)} data-testid={`bb-level-row-${d.id}`}>
                    <td className="py-2.5 pr-3 font-semibold">{d.sim?.compte || d.snapshot_entree?.compte}</td>
                    {!level && <td className="pr-3"><LevelBadge level={d.niveau} /></td>}
                    {showCommercial && !person && <td className="pr-3 text-slate-500">{d.commercial_name}</td>}
                    <td className={`pr-3 ${d.alerts?.date_manquante ? 'text-[#A1281D] font-semibold' : ''}`}>{d.sim?.date_signature_estimee ? fmtDate(d.sim.date_signature_estimee) : 'À compléter'}</td>
                    <td className="pr-3">{fmtDate(d.sim?.date_ouverture)}</td>
                    <td className="pr-3 text-right">{d.sim?.commission ?? '—'} %</td>
                    <td className="text-right font-bold">{fmtK(d.sim?.run_rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
